import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { motion } from 'framer-motion';
import { useOrganizersStore } from '../../stores/organizersStore';
import { RefreshCw, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  organizers: Record<string, any>;
};

export const SyncOrganizerNamesModal = ({ isOpen, onClose, organizers }: Props) => {
  const { translations } = useLanguage();
  const { syncOrganizerName } = useOrganizersStore();
  const [selectedOrganizerId, setSelectedOrganizerId] = useState<string>('');
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [currentProgress, setCurrentProgress] = useState<{ current: number, total: number }>({
    current: 0,
    total: 0
  });
  const [syncResults, setSyncResults] = useState<Record<string, { updated: number, status: 'success' | 'error', error?: string }>>({});
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOrganizerId('');
      setIsAllSelected(false);
      setIsSyncing(false);
      setCurrentProgress({ current: 0, total: 0 });
      setSyncResults({});
    }
  }, [isOpen]);
  
  const handleSync = async () => {
    if (!selectedOrganizerId && !isAllSelected) {
      toast.error('Please select an organizer or choose to sync all');
      return;
    }
    
    setIsSyncing(true);
    
    try {
      if (isAllSelected) {
        // Sync all organizers sequentially
        const organizersArray = Object.values(organizers);
        setCurrentProgress({ current: 0, total: organizersArray.length });
        
        for (let i = 0; i < organizersArray.length; i++) {
          const organizer = organizersArray[i];
          setCurrentProgress({ current: i + 1, total: organizersArray.length });
          
          try {
            const result = await syncOrganizerName(organizer.id);
            setSyncResults(prev => ({
              ...prev,
              [organizer.id]: { updated: result.updated, status: 'success' }
            }));
          } catch (error: any) {
            setSyncResults(prev => ({
              ...prev,
              [organizer.id]: { updated: 0, status: 'error', error: error.message }
            }));
          }
        }
        
        toast.success('Completed syncing all organizer names');
      } else {
        // Sync just one organizer
        setCurrentProgress({ current: 0, total: 1 });
        
        try {
          const result = await syncOrganizerName(selectedOrganizerId);
          setSyncResults({
            [selectedOrganizerId]: { updated: result.updated, status: 'success' }
          });
          setCurrentProgress({ current: 1, total: 1 });
          toast.success('Organizer name synchronized successfully');
        } catch (error: any) {
          setSyncResults({
            [selectedOrganizerId]: { updated: 0, status: 'error', error: error.message }
          });
          toast.error('Failed to sync organizer name');
        }
      }
    } finally {
      setIsSyncing(false);
    }
  };
  
  // Calculate total records updated
  const totalUpdated = Object.values(syncResults)
    .reduce((sum, result) => sum + (result.status === 'success' ? result.updated : 0), 0);
  
  // Count success and error organizers
  const successCount = Object.values(syncResults).filter(result => result.status === 'success').length;
  const errorCount = Object.values(syncResults).filter(result => result.status === 'error').length;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={translations?.syncOrganizerNames?.title || "Sync Organizer Names"}
    >
      <div className="space-y-6">
        {/* Description */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-blue-700">
            {translations?.syncOrganizerNames?.description || 
              "This will update all references to the organizer's name in the system, including links, pilgrim selectors, and guides."}
          </p>
        </div>
        
        {!isSyncing && Object.keys(syncResults).length === 0 && (
          <>
            {/* Selection Options */}
            <div className="space-y-4">
              {/* All Organizers Option */}
              <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input 
                  type="radio"
                  checked={isAllSelected}
                  onChange={() => {
                    setIsAllSelected(true);
                    setSelectedOrganizerId('');
                  }}
                  className="h-4 w-4 text-primary-500 focus:ring-primary-500"
                />
                <div>
                  <div className="font-medium">
                    {translations?.syncOrganizerNames?.syncAll || "Sync All Organizers"}
                  </div>
                  <div className="text-sm text-gray-500">
                    {translations?.syncOrganizerNames?.syncAllDescription || 
                      "Update name references for all organizers in the system (may take some time)"}
                  </div>
                </div>
              </label>
              
              {/* Specific Organizer Option */}
              <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input 
                  type="radio"
                  checked={!isAllSelected}
                  onChange={() => {
                    setIsAllSelected(false);
                    setSelectedOrganizerId('');
                  }}
                  className="h-4 w-4 text-primary-500 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <div className="font-medium mb-2">
                    {translations?.syncOrganizerNames?.syncSpecific || "Sync Specific Organizer"}
                  </div>
                  <select
                    value={selectedOrganizerId}
                    onChange={(e) => setSelectedOrganizerId(e.target.value)}
                    disabled={isAllSelected}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                      isAllSelected ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">
                      {translations?.syncOrganizerNames?.selectOrganizer || "Select an organizer"}
                    </option>
                    {Object.values(organizers).map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              </label>
            </div>
          </>
        )}
        
        {/* Progress */}
        {isSyncing && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <RefreshCw size={20} className="animate-spin text-primary-500" />
              <div className="font-medium">
                {isAllSelected 
                  ? `${translations?.syncOrganizerNames?.syncingAll || "Syncing all organizers"} (${currentProgress.current}/${currentProgress.total})` 
                  : translations?.syncOrganizerNames?.syncingOrganizer || "Syncing organizer name"}
              </div>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary-500 transition-all duration-300"
                style={{ width: `${(currentProgress.current / Math.max(currentProgress.total, 1)) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
        
        {/* Results */}
        {!isSyncing && Object.keys(syncResults).length > 0 && (
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <Check size={20} className="text-green-500" />
              <div className="font-medium text-green-700">
                {translations?.syncOrganizerNames?.syncComplete || "Sync Complete"}
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="text-sm text-green-700">
                {translations?.syncOrganizerNames?.organizersProcessed || "Organizers processed"}: {Object.keys(syncResults).length}
              </div>
              <div className="text-sm text-green-700">
                {translations?.syncOrganizerNames?.successful || "Successful"}: {successCount}
              </div>
              {errorCount > 0 && (
                <div className="text-sm text-red-700">
                  {translations?.syncOrganizerNames?.failed || "Failed"}: {errorCount}
                </div>
              )}
              <div className="text-sm text-green-700">
                {translations?.syncOrganizerNames?.recordsUpdated || "Total records updated"}: {totalUpdated}
              </div>
            </div>
            
            {/* Detailed Results (if multiple organizers) */}
            {Object.keys(syncResults).length > 1 && (
              <div className="mt-4 border-t pt-4">
                <div className="font-medium mb-2 text-green-700">
                  {translations?.syncOrganizerNames?.detailedResults || "Detailed Results:"}
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {Object.entries(syncResults).map(([orgId, result]) => (
                    <div key={orgId} className="flex items-center justify-between py-1 border-b last:border-b-0">
                      <div className="text-sm">
                        {organizers[orgId]?.name || orgId}
                      </div>
                      {result.status === 'success' ? (
                        <div className="text-sm text-green-600">
                          {result.updated} {translations?.syncOrganizerNames?.recordsUpdated || "records updated"}
                        </div>
                      ) : (
                        <div className="text-sm text-red-500">
                          {translations?.syncOrganizerNames?.failed || "Failed"}: {result.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {Object.keys(syncResults).length > 0 
              ? (translations?.common?.close || "Close") 
              : (translations?.common?.cancel || "Cancel")}
          </button>
          
          {!isSyncing && Object.keys(syncResults).length === 0 && (
            <button
              onClick={handleSync}
              disabled={(!selectedOrganizerId && !isAllSelected) || isSyncing}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white
                ${(!selectedOrganizerId && !isAllSelected) || isSyncing
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-primary-500 hover:bg-primary-600'}`}
            >
              <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
              <span>
                {isSyncing 
                  ? (translations?.syncOrganizerNames?.syncing || "Syncing...") 
                  : (translations?.syncOrganizerNames?.sync || "Sync Now")}
              </span>
            </button>
          )}
          
          {!isSyncing && Object.keys(syncResults).length > 0 && (
            <button
              onClick={() => {
                setIsAllSelected(false);
                setSelectedOrganizerId('');
                setSyncResults({});
              }}
              className="flex items-center gap-2 px-6 py-2 rounded-lg text-white bg-primary-500 hover:bg-primary-600"
            >
              <RefreshCw size={18} />
              <span>{translations?.syncOrganizerNames?.syncAnother || "Sync Another"}</span>
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};